import { useState, useEffect } from "react";
import { Navbar, Jumbotron, Container } from "react-bootstrap";

import { search, DEFAULT_MUSEUM } from "./api";
import Search from "./Search";
import SearchResults from "./SearchResults";

import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

const SEARCH_DEBOUNCE_MS = 250;

function App() {
  const [results, setResults] = useState(null);
  const [query, setQuery] = useState("");
  const [museum, setMuseum] = useState(DEFAULT_MUSEUM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset request state whenever the query or museum changes
    setError(null);
    setLoading(false);

    // Clear the results if the user gets rid of the search query
    if (!query) {
      setResults(null);
      return;
    }

    // Don't bother searching for anything less than 3 characters
    if (query.length < 3) {
      return;
    }

    // SECURITY: Limit input length to prevent potential DoS from extremely long strings
    if (query.length > 100) {
      return;
    }

    // Otherwise, start a debounced search. Superseded searches are aborted so
    // a slow response for an old query can never overwrite newer results.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      setLoading(true);
      search(museum, query, { signal: controller.signal })
        .then((artworks) => {
          setResults(artworks);
          // 🛡️ SECURITY: Removed console.log to prevent potential sensitive data leakage
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          // 🛡️ SECURITY: Log securely to internal logger in real app, prevent leaking error details to UI
          setError(new Error("An error occurred during search."));
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, museum]);

  return (
    <div className="App">
      <header>
        <Navbar bg="dark" variant="dark">
          <Navbar.Brand>Ycechung Artwork</Navbar.Brand>
        </Navbar>
        <Jumbotron fluid className="painting-background">
          <Container className="text-center">
            <h1>Find Art You Love</h1>
            <Search
              query={query}
              onChange={(e) => setQuery(e.target.value)}
              museum={museum}
              onMuseumChange={(e) => setMuseum(e.target.value)}
            />
          </Container>
        </Jumbotron>
      </header>
      <main>
        <Container fluid>
          {error ? (
            <p>Unable to retrieve results.</p>
          ) : (
            <SearchResults results={results} loading={loading} />
          )}
        </Container>
      </main>
    </div>
  );
}

export default App;
